/**
 * Preference Normalization Utilities
 * 
 * Ensures buyer preferences are stored and displayed consistently,
 * normalizing legacy values to current standards.
 */

/**
 * Valid Emirates (source of truth)
 */
export const VALID_EMIRATES = [
  'Dubai',
  'Abu Dhabi',
  'Sharjah',
  'Ras Al Khaimah',
  'Umm Al Quwain'
] as const;

export type Emirates = typeof VALID_EMIRATES[number];

/**
 * Maps legacy location values to emirates
 * Handles cases like "Dubai Hills" -> "Dubai", "Dubai Marina" -> "Dubai", etc.
 */
const LOCATION_TO_EMIRATE_MAP: Record<string, Emirates> = {
  // Dubai areas -> Dubai
  'dubai hills': 'Dubai',
  'dubai marina': 'Dubai',
  'downtown dubai': 'Dubai',
  'palm jumeirah': 'Dubai',
  'business bay': 'Dubai',
  'jvc': 'Dubai',
  'jumeirah village circle': 'Dubai',
  'dubai hills estate': 'Dubai',
  'dubai': 'Dubai',
  
  // Abu Dhabi areas -> Abu Dhabi
  'abu dhabi': 'Abu Dhabi',
  'yas island': 'Abu Dhabi',
  'saadiyat island': 'Abu Dhabi',
  
  // Sharjah
  'sharjah': 'Sharjah',
  
  // Ras Al Khaimah
  'ras al khaimah': 'Ras Al Khaimah',
  'rak': 'Ras Al Khaimah',
  
  // Umm Al Quwain
  'umm al quwain': 'Umm Al Quwain',
  'uaq': 'Umm Al Quwain'
};

/**
 * Normalizes preferred locations to emirates-only values
 * 
 * Rules:
 * - Maps legacy area names to their emirate (e.g., "Dubai Hills" -> "Dubai")
 * - Removes values that cannot be mapped to valid emirates
 * - Deduplicates results
 * - Returns only valid emirates
 * 
 * @param locations - Array of location strings (may include legacy values)
 * @returns Normalized array containing only valid emirates
 */
export function normalizePreferredLocations(locations: string[]): Emirates[] {
  if (!Array.isArray(locations) || locations.length === 0) {
    return [];
  }
  
  const normalized = new Set<Emirates>();
  
  for (const loc of locations) {
    if (!loc || typeof loc !== 'string') continue;
    
    const normalizedKey = loc.toLowerCase().trim();
    
    // Direct match in map
    if (LOCATION_TO_EMIRATE_MAP[normalizedKey]) {
      normalized.add(LOCATION_TO_EMIRATE_MAP[normalizedKey]);
      continue;
    }
    
    // Check if it contains an emirate name (for cases like "Dubai Hills Estate")
    for (const [key, emirate] of Object.entries(LOCATION_TO_EMIRATE_MAP)) {
      if (normalizedKey.includes(key) || key.includes(normalizedKey)) {
        normalized.add(emirate);
        break;
      }
    }
    
    // Check if it's already a valid emirate (case-insensitive)
    const validEmirate = VALID_EMIRATES.find(e => 
      e.toLowerCase() === normalizedKey
    );
    if (validEmirate) {
      normalized.add(validEmirate);
    }
  }
  
  // Return only valid emirates, sorted for consistency
  const result = Array.from(normalized).filter(e => VALID_EMIRATES.includes(e));
  return result.sort((a, b) => {
    const indexA = VALID_EMIRATES.indexOf(a);
    const indexB = VALID_EMIRATES.indexOf(b);
    return indexA - indexB;
  });
}

/**
 * Validates and normalizes preferred locations before saving
 * Ensures only valid emirates are saved to the database
 * 
 * @param locations - Array of location strings from UI
 * @returns Normalized array ready for database storage
 */
export function validateAndNormalizeLocationsForSave(locations: string[]): string[] {
  const normalized = normalizePreferredLocations(locations);
  
  if (import.meta.env.DEV && locations.length !== normalized.length) {
    console.log('[PreferenceNormalizer] Locations normalized:', {
      input: locations,
      output: normalized,
      removed: locations.filter(l => !normalized.includes(l as Emirates))
    });
  }
  
  return normalized;
}

/**
 * Normalizes preferred locations when loading from database
 * Handles legacy values that may exist in old profile records
 * 
 * @param locations - Array of location strings from database
 * @returns Normalized array ready for UI display
 */
export function normalizeLocationsOnLoad(locations: string[] | null | undefined): Emirates[] {
  if (!Array.isArray(locations) || locations.length === 0) {
    return [];
  }
  
  return normalizePreferredLocations(locations);
}
